import { useEffect, useRef, useCallback } from 'react';

const POLL_INTERVAL = 60000; // Check every 60 seconds
const RELOAD_DEBOUNCE_KEY = 'versionPollReload';
const RELOAD_DEBOUNCE_MS = 10000; // Prevent reload loops within 10 seconds

/**
 * Hook that polls for app version changes and auto-refreshes when a new deployment is detected.
 *
 * Features:
 * - Polls /version.json every 60 seconds
 * - Checks when tab becomes visible (user returns to app)
 * - Auto-refreshes when new version detected
 * - Prevents infinite reload loops with sessionStorage debounce
 * - Skips polling in development mode
 */
export const useVersionPolling = () => {
  const currentBuildId = useRef(null);
  const isChecking = useRef(false);

  const checkVersion = useCallback(async () => {
    // Skip in development mode
    if (process.env.NODE_ENV === 'development') {
      return;
    }

    // Prevent concurrent checks
    if (isChecking.current) {
      return;
    }

    isChecking.current = true;

    try {
      // Fetch version.json with cache-busting query param
      const response = await fetch(`/version.json?t=${Date.now()}`, {
        cache: 'no-store',
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });

      if (!response.ok) {
        return;
      }

      const data = await response.json();

      if (!data.buildId) {
        return;
      }

      // First load - store the current version
      if (!currentBuildId.current) {
        currentBuildId.current = data.buildId;
        return;
      }

      // Check if version changed
      if (data.buildId !== currentBuildId.current) {
        // New version detected - reload the page
        // Prevent infinite reload loops
        const lastReload = sessionStorage.getItem(RELOAD_DEBOUNCE_KEY);
        const now = Date.now();

        if (lastReload && now - parseInt(lastReload, 10) < RELOAD_DEBOUNCE_MS) {
          return;
        }

        // Mark reload timestamp
        sessionStorage.setItem(RELOAD_DEBOUNCE_KEY, now.toString());

        // Clear any cached data that might cause issues
        if ('caches' in window) {
          caches.keys().then((names) => {
            names.forEach(name => caches.delete(name));
          });
        }

        // Perform the refresh
        window.location.reload();
      }
    } catch (error) {
      // Silent failure - don't disrupt the app if version check fails
    } finally {
      isChecking.current = false;
    }
  }, []);

  useEffect(() => {
    // Skip in development mode
    if (process.env.NODE_ENV === 'development') {
      return;
    }

    // Initial check
    checkVersion();

    // Set up polling interval
    const intervalId = setInterval(checkVersion, POLL_INTERVAL);

    // Also check when user returns to the tab
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkVersion();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup
    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [checkVersion]);
};

export default useVersionPolling;
